import { useState, useEffect } from 'react';
import { message, Modal } from 'antd';
import axios from 'axios';

export const useFPE = () => {
    const [selectedPersonId, setSelectedPersonId] = useState(null); // Currently active person_id
    const [personIdInput, setPersonIdInput] = useState(''); // Input field value
    const [globalSettings, setGlobalSettings] = useState({
        turn_off_fpe: false,
        check_pth: true,
        check_fhaa: false,
        reject_desktop: null,
        green_lane: null,
        allowed_countries: null,
        check_camera: null,
        check_camera_strict: null,
        check_camera_window_switch: null
    });

    const [settings, setSettings] = useState(globalSettings);
    const [loading, setLoading] = useState({ loadingGlobal: true });
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        loadGlobalSettings();
    }, []);

    const loadGlobalSettings = async () => {
        setLoading(prev => ({ ...prev, loadingGlobal: true }));
        try {
            const response = await axios.get("/user/settings/fpe");

            setGlobalSettings(response.data);
            setSettings(response.data);

            message.success('Global settings loaded successfully');
        } catch (error) {
            console.error('Error loading global settings:', error);
            message.error('Failed to load global settings');
        } finally {
            setLoading(prev => ({ ...prev, loadingGlobal: false }));
        }
    };

    const loadConsumerSettings = async (personId) => {
        setLoading(prev => ({ ...prev, loadingSettings: true }));
        try {
            const params = {person_id: personId}
            const response = await axios.get("/user/settings/fpe", { params });

            const loadedSettings = applyGlobalConstraints(response.data);

            setSettings(loadedSettings);
            setSelectedPersonId(personId);
            setHasChanges(false);

            const targetName = `person: ${personId}`;
            message.success(`Settings loaded successfully for ${targetName}`);
        } catch (error) {
            console.error('Error loading settings:', error);
            message.error('Failed to load settings');
        } finally {
            setLoading(prev => ({ ...prev, loadingSettings: false }));
        }
    };

    const applyGlobalConstraints = (loadedSettings) => {
        const result = { ...loadedSettings };
        Object.keys(globalSettings).forEach(key => {
            if (typeof globalSettings[key] !== 'boolean') return;
            if (key === 'turn_off_fpe') {
                if (globalSettings[key] === true) result[key] = true;
            } else if (globalSettings[key] === false) {
                result[key] = false;
            }
        });
        return result;
    };

    const handleSettingChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
        setHasChanges(true);
    };

    const handleToggle = (settingKey) => {
        const globalValue = globalSettings[settingKey];
        if (globalValue === false) {
            return;
        }

        const newValue = !settings[settingKey];
        setLoading(prev => ({ ...prev, [settingKey]: true }));
        handleSettingChange(settingKey, newValue);
        setLoading(prev => ({ ...prev, [settingKey]: false }));
    };

    const saveAllChanges = async () => {
        setLoading(prev => ({ ...prev, saving: true }));
        try {
            const allowedSettings = {
                check_camera: settings.check_camera,
                check_graylist: settings.check_graylist,
                check_blacklist: settings.check_blacklist,
            };

            const requestBody = {
                person_id: selectedPersonId,
                settings: allowedSettings,
            };

            await axios.post("/user/settings/fpe", requestBody);

            setHasChanges(false);

            const targetName = `person ${selectedPersonId}`;
            message.success(`All settings saved successfully for ${targetName}`);
        } catch (error) {
            message.error('Failed to save settings');
        } finally {
            setLoading(prev => ({ ...prev, saving: false }));
        }
    };

    const resetToDefault = async () => {
        const targetName = `person ${selectedPersonId}`;

        Modal.confirm({
            title: 'Reset to Default Settings',
            content: `Are you sure you want to reset all settings for ${targetName} to default values? This action cannot be undone.`,
            okText: 'Yes, Reset',
            cancelText: 'Cancel',
            okType: 'danger',
            onOk: async () => {
                setLoading(prev => ({ ...prev, resetting: true }));
                setSettings(globalSettings);
                setHasChanges(true);
                setLoading(prev => ({ ...prev, resetting: false }));
            }
        });
    };

    const applyPersonId = () => {
        const personId = personIdInput.trim() || null;
        setSelectedPersonId(personId);
        loadConsumerSettings( personId);
    };

    const clearPersonSelection = () => {
        setSelectedPersonId(null);
        setPersonIdInput('');
        setSettings(globalSettings);
        setHasChanges(false);
    };

    return {
        selectedPersonId,
        setSelectedPersonId,
        personIdInput,
        setPersonIdInput,
        settings,
        globalSettings,
        loading,
        hasChanges,
        loadConsumerSettings,
        handleSettingChange,
        handleToggle,
        resetToDefault,
        saveAllChanges,
        applyPersonId,
        clearPersonSelection
    };
};